/**
 * Performance Monitor
 * Tracks batch() effectiveness, RAF scheduling, and store performance
 */

interface PerformanceMetrics {
    batchCount: number;
    batchSavings: number; // How many individual updates were batched
    rafScheduled: number;
    rafExecuted: number;
    persistCalls: number;
    persistDebounced: number; // How many persists were skipped due to debounce
    avgBatchSize: number;
    totalUpdateTime: number;
}

class PerformanceMonitor {
    private metrics: PerformanceMetrics = {
        batchCount: 0,
        batchSavings: 0,
        rafScheduled: 0,
        rafExecuted: 0,
        persistCalls: 0,
        persistDebounced: 0,
        avgBatchSize: 0,
        totalUpdateTime: 0,
    };

    private batchSizes: number[] = [];
    private updateTimes: number[] = [];
    private enabled: boolean;

    constructor() {
        this.enabled = import.meta.env?.PROD || false; // Only in production
    }

    // Track batch usage
    trackBatch(updateCount: number) {
        if (!this.enabled) return;

        this.metrics.batchCount++;
        this.metrics.batchSavings += updateCount - 1; // -1 because 1 batch replaced N updates
        this.batchSizes.push(updateCount);
        this.metrics.avgBatchSize = this.batchSizes.reduce((a, b) => a + b, 0) / this.batchSizes.length;
    }

    // Track RAF scheduling
    trackRAFSchedule() {
        if (!this.enabled) return;
        this.metrics.rafScheduled++;
    }

    trackRAFExecute(_notificationCount: number) {
        if (!this.enabled) return;
        this.metrics.rafExecuted++;
    }

    // Track persistence
    trackPersist() {
        if (!this.enabled) return;
        this.metrics.persistCalls++;
    }

    trackPersistDebounce() {
        if (!this.enabled) return;
        this.metrics.persistDebounced++;
    }

    // Track update performance
    trackUpdate(startTime: number) {
        if (!this.enabled) return;

        const duration = performance.now() - startTime;
        this.updateTimes.push(duration);
        this.metrics.totalUpdateTime += duration;
    }

    // Get current metrics
    getMetrics(): PerformanceMetrics & {
        avgUpdateTime: number;
        persistEfficiency: number; // % of persists saved
        batchEfficiency: number; // % of updates saved
    } {
        const avgUpdateTime = this.updateTimes.length > 0
            ? this.updateTimes.reduce((a, b) => a + b, 0) / this.updateTimes.length
            : 0;

        const totalPossiblePersists = this.metrics.persistCalls + this.metrics.persistDebounced;
        const persistEfficiency = totalPossiblePersists > 0
            ? (this.metrics.persistDebounced / totalPossiblePersists) * 100
            : 0;

        const totalIndividualUpdates = this.metrics.batchSavings + this.metrics.batchCount;
        const batchEfficiency = totalIndividualUpdates > 0
            ? (this.metrics.batchSavings / totalIndividualUpdates) * 100
            : 0;

        return {
            ...this.metrics,
            avgUpdateTime,
            persistEfficiency,
            batchEfficiency,
        };
    }

    // Report to console (for debugging)
    report() {
        if (!this.enabled) {
            console.log('[PerfMonitor] Disabled in development');
            return;
        }

        const metrics = this.getMetrics();

        console.group('📊 ReactiveStore Performance Report');
        console.log(`Batches: ${metrics.batchCount} (avg size: ${metrics.avgBatchSize.toFixed(1)})`);
        console.log(`Batch Efficiency: ${metrics.batchEfficiency.toFixed(1)}% of updates batched`);
        console.log(`RAF Scheduled: ${metrics.rafScheduled}, Executed: ${metrics.rafExecuted}`);
        console.log(`Persist Calls: ${metrics.persistCalls}, Debounced: ${metrics.persistDebounced}`);
        console.log(`Persist Efficiency: ${metrics.persistEfficiency.toFixed(1)}% saved`);
        console.log(`Avg Update Time: ${metrics.avgUpdateTime.toFixed(2)}ms`);
        console.groupEnd();
    }

    // Send to analytics (posthog, etc)
    sendToAnalytics() {
        if (!this.enabled) return;

        const metrics = this.getMetrics();

        // Send to posthog if available
        if (typeof window !== 'undefined' && window.posthog) {
            window.posthog.capture('store_performance', {
                batch_count: metrics.batchCount,
                batch_efficiency: metrics.batchEfficiency,
                persist_efficiency: metrics.persistEfficiency,
                avg_update_time: metrics.avgUpdateTime,
                raf_executed: metrics.rafExecuted,
            });
        }
    }

    // Reset metrics
    reset() {
        this.metrics = {
            batchCount: 0,
            batchSavings: 0,
            rafScheduled: 0,
            rafExecuted: 0,
            persistCalls: 0,
            persistDebounced: 0,
            avgBatchSize: 0,
            totalUpdateTime: 0,
        };
        this.batchSizes = [];
        this.updateTimes = [];
    }

    // Enable/disable monitoring
    setEnabled(enabled: boolean) {
        this.enabled = enabled;
    }
}

// Singleton instance
export const performanceMonitor = new PerformanceMonitor();

// Auto-report every 5 minutes in production
if (typeof window !== 'undefined' && import.meta.env?.PROD) {
    setInterval(() => {
        performanceMonitor.report();
        performanceMonitor.sendToAnalytics();
    }, 5 * 60 * 1000);
}

// Expose globally for debugging
if (typeof window !== 'undefined') {
    window.storePerformance = performanceMonitor;
}
